<template>
  <div class="w-full max-w-6xl mx-auto space-y-6">
    <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Snapshot Operations</h1>
        <p class="text-sm text-gray-600 mt-1">Track ingestion and photo usage by tenant, university, and ceremony.</p>
        <p class="mt-1 text-xs text-gray-500">Last updated {{ lastUpdatedLabel }}</p>
      </div>
      <div class="grid gap-3 w-full md:w-[38rem] md:grid-cols-3">
        <label class="block">
          <span class="text-sm font-medium text-gray-700">University</span>
          <select v-model="selectedUniversityId" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" @change="loadUniversity">
            <option value="">All universities</option>
            <option v-for="uni in universities" :key="uni.id" :value="uni.id">{{ uni.name }}</option>
          </select>
        </label>
        <label class="block">
          <span class="text-sm font-medium text-gray-700">Ceremony</span>
          <select v-model="selectedCeremonyId" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" :disabled="!selectedUniversityId" @change="changeCeremony">
            <option value="">All ceremonies</option>
            <option v-for="ceremony in ceremonies" :key="ceremony.id" :value="ceremony.id">{{ formatCeremony(ceremony) }}</option>
          </select>
        </label>
        <label class="block">
          <span class="text-sm font-medium text-gray-700">Status</span>
          <select v-model="selectedStatus" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" @change="changeStatus">
            <option value="">All statuses</option>
            <option value="stale">Stale active</option>
            <option value="presigned">Presigned</option>
            <option value="running">Running</option>
            <option value="skipped">Skipped</option>
            <option value="complete">Complete</option>
            <option value="failed">Failed</option>
          </select>
        </label>
      </div>
    </div>
    <div class="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-4 py-3">
      <div>
        <div class="text-sm font-medium text-gray-900">Current pressure</div>
        <div :class="bottleneck.class" class="mt-1 text-sm">{{ bottleneck.message }}</div>
      </div>
      <div class="flex items-center gap-3">
        <label class="inline-flex items-center gap-2 text-sm text-gray-700">
          <input v-model="autoRefresh" type="checkbox" class="rounded border-gray-300" @change="toggleAutoRefresh">
          Auto refresh
        </label>
        <button class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50" type="button" :disabled="refreshing" @click="manualRefresh">
          {{ refreshing ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div v-if="error" class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{{ error }}</div>

    <section v-if="operationAlerts.length" class="space-y-2">
      <div
        v-for="alert in operationAlerts"
        :key="alert.title"
        :class="alert.level === 'critical' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'"
        class="rounded-md border p-3 text-sm"
      >
        <div class="font-medium">{{ alert.title }}</div>
        <div class="mt-1">{{ alert.message }}</div>
      </div>
    </section>

    <section class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-200">
        <h2 class="font-semibold text-gray-900">Selected Scope Health</h2>
        <p class="text-xs text-gray-500">Universal Snapshot readiness for the selected tenant, university, or ceremony.</p>
      </div>
      <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
        <div v-for="card in selectedScopeCards" :key="card.label" class="border border-gray-200 rounded-lg p-4">
          <div class="text-sm text-gray-500">{{ card.label }}</div>
          <div class="mt-1 text-2xl font-semibold text-gray-900">{{ card.value }}</div>
          <div v-if="card.detail" class="mt-1 text-xs text-gray-500">{{ card.detail }}</div>
        </div>
      </div>
    </section>
    <div v-if="photoHealth.partial" class="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
      {{ photoHealthPartialMessage }}
    </div>

    <section v-if="hasGfpContext" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-200">
        <h2 class="font-semibold text-gray-900">GFP Pipeline</h2>
        <p class="text-xs text-gray-500">Only applies to GFP-backed photo ingestion for the selected scope.</p>
      </div>
      <div class="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 p-4">
        <div v-for="card in gfpPipelineCards" :key="card.label" class="border border-gray-200 rounded-lg p-4">
          <div class="text-sm text-gray-500">{{ card.label }}</div>
          <div class="mt-1 text-2xl font-semibold text-gray-900">{{ card.value }}</div>
          <div v-if="card.detail" class="mt-1 text-xs text-gray-500">{{ card.detail }}</div>
        </div>
      </div>
    </section>

    <section v-if="photoHealthCeremonies.length" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-200">
        <h2 class="font-semibold text-gray-900">Photo Health By Ceremony</h2>
        <p class="text-xs text-gray-500">Use this to find ceremonies that need review before graduates search or order.</p>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 text-left text-gray-500">
            <tr>
              <th class="px-4 py-2 font-medium">Ceremony</th>
              <th class="px-4 py-2 font-medium">Total</th>
              <th class="px-4 py-2 font-medium">Ready</th>
              <th class="px-4 py-2 font-medium">Failed</th>
              <th class="px-4 py-2 font-medium">Hidden</th>
              <th class="px-4 py-2 font-medium">Sources</th>
              <th class="px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="ceremony in photoHealthCeremonies" :key="ceremony.ceremony_id" class="align-top">
              <td class="px-4 py-3">
                <div class="font-medium text-gray-900">{{ ceremonyHealthLabel(ceremony) }}</div>
                <div class="font-mono text-xs text-gray-500 break-all">{{ ceremony.ceremony_id }}</div>
                <div v-if="ceremony.partial" class="mt-1 text-xs text-amber-700">Capped lower-bound counts</div>
              </td>
              <td class="px-4 py-3">{{ ceremonyHealthValue(ceremony, 'total') }}</td>
              <td class="px-4 py-3">{{ ceremonyHealthValue(ceremony, 'ready') }}</td>
              <td class="px-4 py-3" :class="Number(ceremony.failed || 0) ? 'text-red-700 font-medium' : ''">{{ ceremonyHealthValue(ceremony, 'failed') }}</td>
              <td class="px-4 py-3">{{ ceremonyHealthValue(ceremony, 'hidden') }}</td>
              <td class="px-4 py-3 text-xs text-gray-600">
                <div>Manual {{ formatNumber(ceremony.manual) }}</div>
                <div>GFP {{ formatNumber(ceremony.gfp) }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-2">
                  <router-link
                    :to="ceremonyPhotoSetPath(ceremony)"
                    class="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    View photo set
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div v-for="card in activityCards" :key="card.label" class="bg-white border border-gray-200 rounded-lg p-4">
        <div class="text-sm text-gray-500">{{ card.label }}</div>
        <div class="mt-1 text-2xl font-semibold text-gray-900">{{ card.value }}</div>
        <div v-if="card.detail" class="mt-1 text-xs text-gray-500">{{ card.detail }}</div>
      </div>
    </section>

    <section v-if="hasGfpContext && queueCards.length" class="grid md:grid-cols-2 gap-4">
      <div v-for="queue in queueCards" :key="queue.label" class="bg-white border border-gray-200 rounded-lg p-4">
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="text-sm text-gray-500">{{ queue.label }}</div>
            <div class="mt-1 text-2xl font-semibold text-gray-900">{{ queue.visible }}</div>
          </div>
          <span :class="queue.statusClass" class="rounded-full px-2 py-1 text-xs font-medium">{{ queue.status }}</span>
        </div>
        <div class="mt-2 text-xs text-gray-500">
          In flight {{ queue.inFlight }} · Oldest {{ queue.oldestAge }}
        </div>
      </div>
    </section>

    <section class="bg-white border border-gray-200 rounded-lg p-4">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 class="font-semibold text-gray-900">Review Actions</h2>
          <p class="text-xs text-gray-500">Jump from operational counts to the photo review tools.</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <router-link
            to="/admin/snapshot-problems"
            class="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
          >
            Review failed photos
          </router-link>
          <router-link
            v-if="selectedCeremonyId"
            :to="photoSetReviewPath"
            class="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
          >
            View selected photo set
          </router-link>
        </div>
      </div>
      <p v-if="!selectedCeremonyId" class="mt-3 text-xs text-gray-500">
        Select a ceremony to open its photo set directly.
      </p>
    </section>

    <section class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
        <div>
          <h2 class="font-semibold text-gray-900">Ingestion Jobs</h2>
          <p class="text-xs text-gray-500">Manual uploads and GFP polling jobs for the selected scope.</p>
        </div>
        <button class="text-sm text-blue-700 hover:text-blue-900" type="button" @click="loadOperations">Refresh</button>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 text-left text-gray-500">
            <tr>
              <th class="px-4 py-2 font-medium">Job</th>
              <th class="px-4 py-2 font-medium">Source</th>
              <th class="px-4 py-2 font-medium">Status</th>
              <th class="px-4 py-2 font-medium">Scope</th>
              <th class="px-4 py-2 font-medium">Checkpoint</th>
              <th class="px-4 py-2 font-medium">Counts</th>
              <th class="px-4 py-2 font-medium">Updated</th>
              <th class="px-4 py-2 font-medium">Issue</th>
              <th class="px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="job in jobs" :key="job.job_id" class="align-top">
              <td class="px-4 py-3 font-mono text-xs max-w-xs break-all">{{ job.job_id }}</td>
              <td class="px-4 py-3 uppercase text-xs tracking-wide">{{ job.source }}</td>
              <td class="px-4 py-3">
                <span :class="statusClass(job.status)" class="inline-flex rounded-full px-2 py-1 text-xs font-medium">{{ job.status }}</span>
              </td>
              <td class="px-4 py-3 text-xs">
                <div>{{ job.university_id || 'All universities' }}</div>
                <div class="text-gray-500">{{ job.ceremony_id || 'All ceremonies' }}</div>
              </td>
              <td class="px-4 py-3 text-xs">
                <div>{{ jobFilter(job) }}</div>
                <div v-if="job.details && job.details.created_after" class="text-gray-500">From {{ formatDate(job.details.created_after) }}</div>
                <div v-if="job.details && job.details.newest_created" class="text-gray-500">Latest {{ formatDate(job.details.newest_created) }}</div>
                <div v-if="jobHasBacklog(job)" class="text-amber-700">{{ jobRunLimitLabel(job) }}</div>
                <div v-if="jobHitProviderScanLimit(job)" class="text-amber-700">Provider scan cap reached; more source rows may remain.</div>
                <div v-if="jobHitTimeBudget(job)" class="text-amber-700">Time budget reached; polling will continue next run.</div>
                <div v-if="job.details && job.details.skipped_reason" class="text-amber-700">{{ job.details.skipped_reason }}</div>
              </td>
              <td class="px-4 py-3 text-xs">
                <div v-if="jobUsesQueuedDownloads(job)">
                  Provider fetched {{ formatNumber(jobFetchedOrSeenCount(job)) }} · Downloads queued {{ formatNumber(jobEnqueuedCount(job)) }}
                </div>
                <div v-else>Source seen {{ job.seen_count }} · Uploaded {{ jobUploadedCount(job) }}</div>
                <div class="text-gray-500">Job indexed snapshot {{ job.stored_count }} · Skipped {{ job.skipped_count }} · Failed {{ job.failed_count }}</div>
                <div v-if="jobUsesQueuedDownloads(job)" class="text-gray-500">Download worker uploads images before backend indexing</div>
                <div v-if="jobStoreAttempts(job)" class="text-gray-500">Store attempts {{ jobStoreAttempts(job) }}</div>
                <div v-if="jobRecordedFailures(job)" class="text-amber-700">Recorded failures {{ jobRecordedFailures(job) }} · recoverable</div>
                <div v-if="jobUnrecordedFailures(job)" class="text-red-700">Unrecorded failures {{ jobUnrecordedFailures(job) }} · checkpoint blocked</div>
                <div v-if="jobAwaitingIndexCount(job)" class="text-amber-700">{{ jobAwaitingIndexCount(job) }} uploaded photo{{ jobAwaitingIndexCount(job) === 1 ? '' : 's' }} awaiting indexing</div>
                <div v-if="jobDeferredCount(job)" class="text-amber-700">Fetched {{ jobFetchedCount(job) }} · Deferred {{ jobDeferredCount(job) }}</div>
                <div v-if="jobProgress(job)" class="mt-1">
                  <div class="h-1.5 w-28 rounded-full bg-gray-100">
                    <div class="h-1.5 rounded-full bg-blue-600" :style="{ width: jobProgress(job) + '%' }"></div>
                  </div>
                  <div class="mt-0.5 text-gray-500">{{ jobProgress(job) }}% of expected</div>
                </div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-600">{{ formatDate(job.updated_at) }}</td>
              <td class="px-4 py-3 text-xs max-w-xs" :class="jobIssueClass(job)">{{ jobIssue(job) }}</td>
              <td class="px-4 py-3 text-xs">
                <button
                  type="button"
                  class="rounded-md border border-gray-300 bg-white px-2 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  :disabled="manifestLoading && selectedManifestJobId === job.job_id"
                  @click="loadJobManifest(job)"
                >
                  {{ manifestLoading && selectedManifestJobId === job.job_id ? 'Loading...' : 'View photos' }}
                </button>
                <button
                  v-if="canAbandonJob(job)"
                  type="button"
                  class="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                  :disabled="abandoningJobId === job.job_id"
                  @click="abandonJob(job)"
                >
                  {{ abandoningJobId === job.job_id ? 'Retiring...' : 'Retire stale job' }}
                </button>
              </td>
            </tr>
            <tr v-if="!jobs.length && !loading">
              <td class="px-4 py-5 text-gray-500" colspan="9">No ingestion jobs found.</td>
            </tr>
            <tr v-if="loading">
              <td class="px-4 py-5 text-gray-500" colspan="9">Loading snapshot operations...</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="selectedManifestJobId" class="border-t border-gray-200 bg-gray-50 px-5 py-4">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 class="font-semibold text-gray-900">Job Photo Manifest</h3>
            <p class="mt-1 font-mono text-xs text-gray-600 break-all">{{ selectedManifestJobId }}</p>
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              class="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              :disabled="retryingJobFailed"
              @click="retryFailedManifestBatch"
            >
              {{ retryingJobFailed ? 'Retrying...' : retryFailedCursor ? 'Continue failed retry' : 'Retry failed batch' }}
            </button>
            <button
              type="button"
              class="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              :disabled="queueingJobRetry"
              @click="queueFailedManifestRetry"
            >
              {{ queueingJobRetry ? 'Queueing...' : 'Queue full retry' }}
            </button>
            <button
              v-if="manifestCursor"
              type="button"
              class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              @click="loadJobManifestById(selectedManifestJobId, manifestCursor, true)"
            >
              Load more
            </button>
            <button type="button" class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" @click="clearManifest">
              Close
            </button>
          </div>
        </div>
        <div v-if="manifestError" class="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{{ manifestError }}</div>
        <div v-if="retryJobFailedResult" class="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          {{ retryJobFailedResult }}
        </div>
        <div v-if="manifestLoading" class="mt-3 text-sm text-gray-500">Loading photo manifest...</div>
        <div v-else-if="!manifestPhotos.length" class="mt-3 text-sm text-gray-500">No photo metadata found for this job.</div>
        <div v-else class="mt-3 overflow-x-auto rounded-md border border-gray-200 bg-white">
          <table class="min-w-full text-xs">
            <thead class="bg-gray-50 text-left text-gray-500">
              <tr>
                <th class="px-3 py-2 font-medium">Photo</th>
                <th class="px-3 py-2 font-medium">Status</th>
                <th class="px-3 py-2 font-medium">Source</th>
                <th class="px-3 py-2 font-medium">File</th>
                <th class="px-3 py-2 font-medium">Updated</th>
                <th class="px-3 py-2 font-medium">Issue</th>
                <th class="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="photo in manifestPhotos" :key="photo.photo_id" class="align-top">
                <td class="px-3 py-2 font-mono break-all">{{ photo.photo_id }}</td>
                <td class="px-3 py-2">
                  <span :class="photoStatusClass(photo.status)" class="inline-flex rounded-full px-2 py-1 text-xs font-medium">{{ photo.status }}</span>
                </td>
                <td class="px-3 py-2 uppercase">{{ photo.source || '' }}</td>
                <td class="px-3 py-2">{{ photo.file_label || 'image' }}</td>
                <td class="px-3 py-2 text-gray-600">{{ formatDate(photo.updated_at) }}</td>
                <td class="px-3 py-2 text-red-700">{{ photo.failure_reason || '' }}</td>
                <td class="px-3 py-2">
                  <div class="flex flex-wrap gap-2">
                    <router-link
                      v-if="photo.collection_id"
                      :to="photoReviewPath(photo)"
                      class="rounded-md border border-gray-300 bg-white px-2 py-1 text-gray-700 hover:bg-gray-50"
                    >
                      Review set
                    </router-link>
                    <button
                      v-if="photo.status === 'failed'"
                      type="button"
                      class="rounded-md border border-blue-300 bg-blue-50 px-2 py-1 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                      :disabled="retryingPhotoId === photo.photo_id"
                      @click="retryManifestPhoto(photo)"
                    >
                      {{ retryingPhotoId === photo.photo_id ? 'Retrying...' : 'Retry' }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section v-if="isAdmin" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
        <div>
          <h2 class="font-semibold text-gray-900">Recent Audit Events</h2>
          <p class="text-xs text-gray-500">Security-sensitive snapshot admin actions for the selected scope.</p>
        </div>
        <button class="text-sm text-blue-700 hover:text-blue-900" type="button" @click="loadAuditEvents">Refresh</button>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 text-left text-gray-500">
            <tr>
              <th class="px-4 py-2 font-medium">Action</th>
              <th class="px-4 py-2 font-medium">Actor</th>
              <th class="px-4 py-2 font-medium">Resource</th>
              <th class="px-4 py-2 font-medium">Scope</th>
              <th class="px-4 py-2 font-medium">Details</th>
              <th class="px-4 py-2 font-medium">When</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="event in auditEvents" :key="event.audit_id" class="align-top">
              <td class="px-4 py-3">{{ event.action }}</td>
              <td class="px-4 py-3 text-xs">{{ auditActor(event) }}</td>
              <td class="px-4 py-3 font-mono text-xs max-w-xs break-all">{{ auditResource(event) }}</td>
              <td class="px-4 py-3 text-xs">
                <div>{{ event.university_id || 'All universities' }}</div>
                <div class="text-gray-500">{{ event.ceremony_id || 'All ceremonies' }}</div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-600 max-w-sm">{{ auditDetails(event) }}</td>
              <td class="px-4 py-3 text-xs text-gray-600">{{ formatDate(event.created_at) }}</td>
            </tr>
            <tr v-if="!auditEvents.length">
              <td class="px-4 py-5 text-gray-500" colspan="6">No audit events found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { trackEvent } from '../../Analytics.vue'
import { getAccessContext } from '../../admin/access.js'

const FILTER_STORAGE_KEY = 'snapshot.operations.filters'

export default {
  name: 'SnapshotOperations',
  data() {
    return {
      universities: [],
      ceremonies: [],
      selectedUniversityId: '',
      selectedCeremonyId: '',
      selectedStatus: '',
      summary: {},
      photoHealth: {},
      photoHealthCeremonies: [],
      activity: {},
      activityWarning: '',
      auditEvents: [],
      jobs: [],
      loading: false,
      error: '',
      abandoningJobId: '',
      selectedManifestJobId: '',
      manifestPhotos: [],
      manifestCursor: '',
      manifestLoading: false,
      manifestError: '',
      retryingPhotoId: '',
      retryingJobFailed: false,
      queueingJobRetry: false,
      retryFailedCursor: '',
      retryFailedManifestSource: '',
      retryJobFailedResult: '',
      autoRefresh: true,
      refreshTimer: null,
      refreshing: false,
      lastUpdatedAt: '',
    }
  },
  computed: {
    isAdmin() {
      return getAccessContext().roles.includes('admin')
    },
    selectedScopeCards() {
      return [
        { label: 'Search-ready photos', value: this.photoHealthValue(this.photoHealth.ready), detail: 'Indexed metadata photos graduates can find' },
        { label: 'Failed photos', value: this.photoHealthValue(this.photoHealth.failed), detail: 'Need review before ordering' },
        { label: 'Hidden photos', value: this.photoHealthValue(this.photoHealth.hidden), detail: 'Excluded from search and orders' },
        { label: 'Metadata photos total', value: this.photoHealthValue(this.photoHealth.total), detail: 'Current source of truth' },
        { label: 'Uploaded to S3', value: this.summary.uploaded_count || 0, detail: 'Job-level upload count before indexing' },
        { label: 'Active jobs', value: this.summary.active_jobs || 0, detail: this.staleThresholdDetail },
        { label: 'Stale active jobs', value: this.summary.stale_jobs || 0, detail: this.staleThresholdDetail },
        { label: 'Failed jobs', value: this.summary.failed_jobs || 0, detail: 'Failed ingestion runs, not failed photo total' },
      ]
    },
    gfpPipelineCards() {
      return [
        { label: 'GFP jobs', value: this.summary.gfp_jobs || 0, detail: `${this.summary.manual_jobs || 0} manual jobs in this scope` },
        { label: 'Source photos seen', value: this.summary.seen_count || 0, detail: 'Provider rows accepted by ingestion jobs' },
        { label: 'Downloads handed off', value: this.summary.enqueued_count || 0, detail: 'Cumulative GFP images queued for workers' },
        { label: 'Download backlog', value: this.gfpDownloadBacklogCount, detail: 'Current global GFP download queue' },
        { label: 'Oldest active age', value: this.activeJobAgeLabel, detail: this.staleThresholdDetail },
      ]
    },
    hasGfpContext() {
      return Number(this.summary.gfp_jobs || 0) > 0 || this.jobs.some((job) => String(job.source || '').toLowerCase() === 'gfp')
    },
    bottleneck() {
      const staleJobs = Number(this.summary.stale_jobs || 0)
      if (staleJobs > 0) {
        return {
          class: 'text-red-700',
          message: `${staleJobs} stale active job${staleJobs === 1 ? '' : 's'} need review before this scope can be trusted.`,
        }
      }
      const failedPhotos = Number(this.photoHealth.failed || 0)
      if (failedPhotos > 0) {
        return {
          class: 'text-red-700',
          message: `${this.formatNumber(failedPhotos)} failed photo${failedPhotos === 1 ? '' : 's'} need review.`,
        }
      }
      if (this.hasGfpContext && this.gfpDownloadBacklogCount > 0) {
        return {
          class: 'text-amber-700',
          message: `${this.formatNumber(this.gfpDownloadBacklogCount)} GFP download worker message${this.gfpDownloadBacklogCount === 1 ? '' : 's'} are still in flight or queued.`,
        }
      }
      const backlogJobs = this.jobs.filter((job) => this.jobHasBacklog(job) || this.jobHitProviderScanLimit(job) || this.jobHitTimeBudget(job)).length
      if (this.hasGfpContext && backlogJobs > 0) {
        return {
          class: 'text-amber-700',
          message: `${backlogJobs} GFP job${backlogJobs === 1 ? '' : 's'} stopped before fully draining; watch the next run.`,
        }
      }
      return {
        class: 'text-green-700',
        message: 'No current Snapshot pressure is visible for this selected scope.',
      }
    },
    lastUpdatedLabel() {
      return this.lastUpdatedAt ? this.formatDate(this.lastUpdatedAt) : 'not yet'
    },
    photoHealthPartialMessage() {
      const scannedLimit = Number(this.photoHealth.scanned_limit || 0)
      if (scannedLimit) {
        return `Photo health is capped after scanning ${this.formatNumber(scannedLimit)} metadata rows for this scope. Counts are lower bounds; narrow to a ceremony for exact counts.`
      }
      return 'Photo health is capped for this scope. Counts are lower bounds; narrow to a ceremony for exact counts.'
    },
    activityCards() {
      return [
        { label: 'Searches', value: this.activity.searches || 0, detail: this.lastActivityDetail(this.activity.last_search_at) },
        { label: 'Orders', value: this.activity.orders || 0, detail: this.lastActivityDetail(this.activity.last_order_at) },
        { label: 'Downloads', value: this.activity.downloads || 0, detail: this.lastActivityDetail(this.activity.last_download_at) },
        { label: 'Audit events', value: this.activity.total_events || 0, detail: this.activity.unavailable ? 'Unavailable' : '' },
      ]
    },
    queueCards() {
      const queues = this.summary.queues || {}
      return [
        this.queueCard('GFP queue', queues.gfp),
        this.queueCard('GFP download queue', queues.gfp_download),
        this.queueCard('Failed retry queue', queues.failed_retry),
      ].filter(Boolean)
    },
    photoSetReviewPath() {
      return `/admin/snapshot-photo-sets/${this.selectedCeremonyId}/photos`
    },
    activeJobAgeLabel() {
      const seconds = Number(this.summary.oldest_active_age_seconds || 0)
      return seconds > 0 ? this.durationLabel(seconds) : 'None'
    },
    summaryAwaitingIndexCount() {
      return Math.max(0, Number(this.summary.uploaded_count || 0) - Number(this.summary.stored_count || 0))
    },
    gfpDownloadBacklogCount() {
      const queue = (this.summary.queues || {}).gfp_download || {}
      return Number(queue.visible || 0) + Number(queue.in_flight || 0)
    },
    staleThresholdDetail() {
      const minutes = Number(this.summary.stale_after_minutes || 0)
      return minutes > 0 ? `Stale after ${this.durationLabel(minutes * 60)}` : ''
    },
    operationAlerts() {
      const alerts = []
      const staleJobs = Number(this.summary.stale_jobs || 0)
      if (staleJobs > 0) {
        alerts.push({
          level: 'critical',
          title: 'Stale ingestion jobs need review',
          message: `${staleJobs} active job${staleJobs === 1 ? '' : 's'} have had no recent progress. Open the job manifest, check S3/object state, then retire or retry from this page.`,
        })
      }
      const backlogJobs = this.jobs.filter((job) => this.jobHasBacklog(job) || this.jobHitProviderScanLimit(job)).length
      if (backlogJobs > 0) {
        alerts.push({
          level: 'warning',
          title: 'GFP backlog may remain',
          message: `${backlogJobs} job${backlogJobs === 1 ? '' : 's'} reached a run or provider scan limit. Check queue depth, fetched/enqueued counts, and the next poll before assuming the source is drained.`,
        })
      }
      const timeBudgetJobs = this.jobs.filter((job) => this.jobHitTimeBudget(job)).length
      if (timeBudgetJobs > 0) {
        alerts.push({
          level: 'warning',
          title: 'GFP polling hit the Lambda time budget',
          message: `${timeBudgetJobs} job${timeBudgetJobs === 1 ? '' : 's'} stopped before draining all fetched photos. Watch the next run and oldest active age.`,
        })
      }
      if (this.activityWarning) {
        alerts.push({
          level: 'warning',
          title: 'Usage activity unavailable',
          message: this.activityWarning,
        })
      }
      this.queueCards.forEach((queue) => {
        if (!queue.problem) return
        alerts.push({
          level: queue.critical ? 'critical' : 'warning',
          title: `${queue.label} needs attention`,
          message: queue.error
            ? `${queue.label} health could not be loaded: ${queue.error}.`
            : `${queue.visible} visible message${queue.visible === '1' ? '' : 's'} and oldest age ${queue.oldestAge}. Check queue alarms if this keeps rising.`,
        })
      })
      return alerts
    },
  },
  created() {
    this.restoreFilters()
    this.loadUniversities()
    this.loadOperations()
    this.startAutoRefresh()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    trackEvent(this.$route.path, { label: 'SNAPSHOT_OPERATIONS_OPENED' })
  },
  beforeDestroy() {
    this.stopAutoRefresh()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    apiBase() {
      return process.env.VUE_APP_SNAPSHOT_API_BASE || ''
    },
    async loadUniversities() {
      try {
        const r = await axios.get('/universities')
        this.universities = r.data || []
        if (this.selectedUniversityId) await this.loadCeremonies()
      } catch (e) {
        this.error = 'Unable to load universities.'
      }
    },
    restoreFilters() {
      try {
        const raw = window.localStorage.getItem(FILTER_STORAGE_KEY)
        if (!raw) return
        const filters = JSON.parse(raw)
        this.selectedUniversityId = filters.university_id || ''
        this.selectedCeremonyId = filters.ceremony_id || ''
        this.selectedStatus = filters.status || ''
      } catch (e) {
        window.localStorage.removeItem(FILTER_STORAGE_KEY)
      }
    },
    saveFilters() {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        university_id: this.selectedUniversityId || '',
        ceremony_id: this.selectedCeremonyId || '',
        status: this.selectedStatus || '',
      }))
    },
    async loadUniversity() {
      this.selectedCeremonyId = ''
      this.saveFilters()
      await this.loadCeremonies()
      await this.loadOperations()
    },
    async loadCeremonies() {
      this.ceremonies = []
      if (this.selectedUniversityId) {
        try {
          const r = await axios.get(`/universities/${this.selectedUniversityId}/ceremonies`)
          this.ceremonies = r.data || []
          if (this.selectedCeremonyId && !this.ceremonies.some((ceremony) => ceremony.id === this.selectedCeremonyId)) {
            this.selectedCeremonyId = ''
            this.saveFilters()
          }
        } catch (e) {
          this.error = 'Unable to load ceremonies.'
        }
      }
    },
    async changeCeremony() {
      this.saveFilters()
      await this.loadOperations()
    },
    async changeStatus() {
      this.saveFilters()
      await this.loadJobs()
    },
    async loadOperations() {
      this.refreshing = true
      const tasks = [this.loadJobs(), this.loadPhotoHealth(), this.loadActivity()]
      if (this.isAdmin) tasks.push(this.loadAuditEvents())
      try {
        await Promise.all(tasks)
        this.lastUpdatedAt = new Date().toISOString()
      } finally {
        this.refreshing = false
      }
    },
    async manualRefresh() {
      await this.loadOperations()
    },
    toggleAutoRefresh() {
      if (this.autoRefresh) {
        this.startAutoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      if (!this.autoRefresh) return
      this.refreshTimer = window.setInterval(() => {
        if (document.hidden || this.refreshing || this.loading || this.manifestLoading) return
        this.loadOperations()
      }, 30000)
    },
    stopAutoRefresh() {
      if (!this.refreshTimer) return
      window.clearInterval(this.refreshTimer)
      this.refreshTimer = null
    },
    handleVisibilityChange() {
      if (!document.hidden && this.autoRefresh) this.loadOperations()
    },
    async loadJobs() {
      this.loading = true
      this.error = ''
      try {
        const params = new URLSearchParams()
        if (this.selectedUniversityId) params.set('university_id', this.selectedUniversityId)
        if (this.selectedCeremonyId) params.set('ceremony_id', this.selectedCeremonyId)
        if (this.selectedStatus === 'stale') {
          params.set('stale', 'true')
        } else if (this.selectedStatus) {
          params.set('status', this.selectedStatus)
        }
        const suffix = params.toString() ? `?${params.toString()}` : ''
        const r = await axios.get(`${this.apiBase()}/admin/snapshot/ingestion-jobs${suffix}`)
        this.jobs = r.data.items || []
        this.summary = r.data.summary || {}
      } catch (e) {
        this.error = 'Unable to load snapshot operations.'
      } finally {
        this.loading = false
      }
    },
    async loadPhotoHealth() {
      try {
        const params = new URLSearchParams()
        if (this.selectedUniversityId) params.set('university_id', this.selectedUniversityId)
        if (this.selectedCeremonyId) params.set('ceremony_id', this.selectedCeremonyId)
        if (this.selectedCeremonyId) params.set('max_items', '30000')
        const suffix = params.toString() ? `?${params.toString()}` : ''
        const r = await axios.get(`${this.apiBase()}/admin/snapshot/usage/photo-health${suffix}`)
        this.photoHealth = r.data.summary || {}
        this.photoHealthCeremonies = r.data.ceremonies || []
      } catch (e) {
        this.error = 'Unable to load snapshot photo health.'
      }
    },
    async loadActivity() {
      try {
        const params = new URLSearchParams()
        if (this.selectedUniversityId) params.set('university_id', this.selectedUniversityId)
        if (this.selectedCeremonyId) params.set('ceremony_id', this.selectedCeremonyId)
        const suffix = params.toString() ? `?${params.toString()}` : ''
        const r = await axios.get(`${this.apiBase()}/admin/snapshot/usage/activity${suffix}`)
        this.activity = r.data.summary || {}
        this.activityWarning = r.data.warning || ''
      } catch (e) {
        this.activityWarning = ''
        this.error = 'Unable to load snapshot activity.'
      }
    },
    async loadAuditEvents() {
      try {
        const params = new URLSearchParams()
        if (this.selectedUniversityId) params.set('university_id', this.selectedUniversityId)
        if (this.selectedCeremonyId) params.set('ceremony_id', this.selectedCeremonyId)
        const suffix = params.toString() ? `?${params.toString()}` : ''
        const r = await axios.get(`${this.apiBase()}/admin/snapshot/audit-events${suffix}`)
        this.auditEvents = r.data.items || []
      } catch (e) {
        this.error = 'Unable to load snapshot audit events.'
      }
    },
    formatCeremony(ceremony) {
      if (!ceremony) return ''
      if (ceremony.label) return ceremony.label
      const value = ceremony.datetime || ceremony.ceremony_datetime || ceremony.id || ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
    formatDate(value) {
      if (!value) return ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return Number.isFinite(number) ? number.toLocaleString() : String(value)
    },
    durationLabel(seconds) {
      const total = Math.max(0, Math.floor(Number(seconds || 0)))
      if (total < 60) return `${total}s`
      const minutes = Math.floor(total / 60)
      if (minutes < 60) return `${minutes}m`
      const hours = Math.floor(minutes / 60)
      const remainder = minutes % 60
      return remainder ? `${hours}h ${remainder}m` : `${hours}h`
    },
    queueCard(label, queue) {
      if (!queue) return null
      if (queue.error) {
        return {
          label,
          visible: '!',
          inFlight: '0',
          oldestAge: 'Unknown',
          status: 'Error',
          error: queue.error,
          statusClass: 'bg-red-50 text-red-700',
          problem: true,
          critical: true,
        }
      }
      const visible = Number(queue.visible || 0)
      const inFlight = Number(queue.in_flight || 0)
      const hasOldestAge = queue.oldest_age_seconds !== null && queue.oldest_age_seconds !== undefined
      const oldest = hasOldestAge ? Number(queue.oldest_age_seconds || 0) : 0
      const problem = visible > 0 || oldest > 900
      return {
        label,
        visible: this.formatNumber(visible),
        inFlight: this.formatNumber(inFlight),
        oldestAge: hasOldestAge ? (oldest > 0 ? this.durationLabel(oldest) : 'None') : 'Unavailable',
        status: problem ? 'Backlog' : 'Clear',
        statusClass: problem ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700',
        problem,
        critical: oldest > 1800,
      }
    },
    photoHealthValue(value) {
      const formatted = this.formatNumber(value)
      return this.photoHealth.partial ? `>= ${formatted}` : formatted
    },
    ceremonyHealthValue(ceremony, key) {
      const formatted = this.formatNumber(ceremony && ceremony[key])
      return ceremony && ceremony.partial ? `>= ${formatted}` : formatted
    },
    ceremonyHealthLabel(ceremony) {
      if (!ceremony) return ''
      if (ceremony.label) return ceremony.label
      return this.formatCeremony({
        id: ceremony.ceremony_id,
        datetime: ceremony.datetime,
        label: ceremony.label,
      })
    },
    ceremonyPhotoSetPath(ceremony) {
      return `/admin/snapshot-photo-sets/${encodeURIComponent(ceremony.ceremony_id)}/photos`
    },
    lastActivityDetail(value) {
      return value ? `Last ${this.formatDate(value)}` : ''
    },
    jobFilter(job) {
      const details = job.details || {}
      if (!details.filter_type) return job.provider_job_id || ''
      return `${details.filter_type}: ${job.provider_job_id || ''}`
    },
    jobProgress(job) {
      const expected = Number(job.expected_count || 0)
      if (!expected) return 0
      const complete = Number(job.stored_count || 0) + Number(job.skipped_count || 0) + Number(job.failed_count || 0)
      return Math.max(0, Math.min(100, Math.round((complete / expected) * 100)))
    },
    jobHasBacklog(job) {
      return !!(job && job.details && job.details.hit_run_limit)
    },
    jobHitProviderScanLimit(job) {
      return !!(job && job.details && job.details.hit_provider_scan_limit)
    },
    jobRunLimitLabel(job) {
      const limit = Number(job && job.details && job.details.max_photos_per_run ? job.details.max_photos_per_run : 0)
      const fetched = this.jobFetchedCount(job)
      const attempts = this.jobStoreAttempts(job)
      const pieces = ['Run limit reached']
      if (attempts) pieces.push(`store attempts ${this.formatNumber(attempts)}`)
      if (fetched) pieces.push(`fetched ${this.formatNumber(fetched)}`)
      if (limit) pieces.push(`limit ${this.formatNumber(limit)}`)
      return `${pieces.join(' · ')}; more source photos may remain.`
    },
    jobHitTimeBudget(job) {
      return !!(job && job.details && job.details.hit_time_budget)
    },
    jobFetchedCount(job) {
      return Number(job && job.details && job.details.fetched_count ? job.details.fetched_count : 0)
    },
    jobFetchedOrSeenCount(job) {
      return this.jobFetchedCount(job) || Number(job && job.seen_count ? job.seen_count : 0)
    },
    jobEnqueuedCount(job) {
      return Number(job && job.details && job.details.enqueued_count ? job.details.enqueued_count : 0)
    },
    jobUsesQueuedDownloads(job) {
      const details = (job && job.details) || {}
      return details.download_mode === 'queued' || this.jobEnqueuedCount(job) > 0
    },
    jobStoreAttempts(job) {
      return Number(job && job.details && job.details.store_attempts ? job.details.store_attempts : 0)
    },
    jobRecordedFailures(job) {
      return Number(job && job.details && job.details.recorded_failures ? job.details.recorded_failures : 0)
    },
    jobUnrecordedFailures(job) {
      return Number(job && job.details && job.details.unrecorded_failures ? job.details.unrecorded_failures : 0)
    },
    jobDeferredCount(job) {
      const fetched = this.jobFetchedCount(job)
      const seen = Number(job && job.seen_count ? job.seen_count : 0)
      return Math.max(0, fetched - seen)
    },
    jobUploadedCount(job) {
      return Number(job && job.details && job.details.uploaded_count ? job.details.uploaded_count : 0)
    },
    jobAwaitingIndexCount(job) {
      return Math.max(0, this.jobUploadedCount(job) - Number(job && job.stored_count ? job.stored_count : 0))
    },
    jobIssue(job) {
      if (this.jobUnrecordedFailures(job)) return 'Unrecorded failures blocking checkpoint'
      if (job.error_message) return job.error_message
      if (this.jobHasBacklog(job)) return 'Run limit reached'
      if (this.jobHitProviderScanLimit(job)) return 'Provider scan cap reached'
      if (this.jobHitTimeBudget(job)) return 'Time budget reached'
      if (job.is_stale) return 'No recent progress'
      return ''
    },
    jobIssueClass(job) {
      if (this.jobUnrecordedFailures(job)) return 'text-red-700'
      if (job.is_stale && !job.error_message) return 'text-amber-700'
      return (this.jobHasBacklog(job) || this.jobHitProviderScanLimit(job) || this.jobHitTimeBudget(job)) && !job.error_message ? 'text-amber-700' : 'text-red-700'
    },
    canAbandonJob(job) {
      return !!(job && job.is_stale && (job.status === 'running' || job.status === 'presigned'))
    },
    async abandonJob(job) {
      if (!this.canAbandonJob(job) || this.abandoningJobId) return
      this.error = ''
      this.abandoningJobId = job.job_id
      try {
        await axios.post(`${this.apiBase()}/admin/snapshot/ingestion-jobs/${encodeURIComponent(job.job_id)}/abandon`, {
          reason: 'Retired from Snapshot Operations after no recent progress',
        })
        await this.loadOperations()
      } catch (e) {
        this.error = 'Unable to retire stale job.'
      } finally {
        this.abandoningJobId = ''
      }
    },
    async loadJobManifest(job) {
      if (!job || !job.job_id) return
      await this.loadJobManifestById(job.job_id)
    },
    async loadJobManifestById(jobId, cursor = '', append = false) {
      this.selectedManifestJobId = jobId
      this.manifestLoading = true
      this.manifestError = ''
      if (!append) {
        this.retryFailedCursor = ''
        this.retryFailedManifestSource = ''
        this.retryJobFailedResult = ''
      }
      try {
        const params = new URLSearchParams()
        params.set('limit', '50')
        if (cursor) params.set('cursor', cursor)
        const r = await axios.get(`${this.apiBase()}/admin/snapshot/ingestion-jobs/${encodeURIComponent(jobId)}/photos?${params.toString()}`)
        const items = r.data.items || []
        this.manifestPhotos = append ? this.manifestPhotos.concat(items) : items
        this.manifestCursor = r.data.cursor || ''
      } catch (e) {
        this.manifestError = 'Unable to load job photo manifest.'
        if (!append) this.manifestPhotos = []
      } finally {
        this.manifestLoading = false
      }
    },
    clearManifest() {
      this.selectedManifestJobId = ''
      this.manifestPhotos = []
      this.manifestCursor = ''
      this.manifestError = ''
      this.retryFailedCursor = ''
      this.retryFailedManifestSource = ''
      this.retryJobFailedResult = ''
    },
    photoReviewPath(photo) {
      return `/admin/snapshot-photo-sets/${encodeURIComponent(photo.collection_id)}/photos`
    },
    async retryManifestPhoto(photo) {
      if (!photo || !photo.photo_id || this.retryingPhotoId) return
      this.retryingPhotoId = photo.photo_id
      this.manifestError = ''
      try {
        const r = await axios.post(
          `${this.apiBase()}/admin/snapshot/photos/${encodeURIComponent(photo.photo_id)}/status`,
          { action: 'retry' }
        )
        const updated = r.data || {}
        this.manifestPhotos = this.manifestPhotos.map((item) => (
          item.photo_id === photo.photo_id ? { ...item, ...updated } : item
        ))
      } catch (e) {
        this.manifestError = 'Unable to retry selected photo.'
      } finally {
        this.retryingPhotoId = ''
      }
    },
    async retryFailedManifestBatch() {
      if (!this.selectedManifestJobId || this.retryingJobFailed) return
      this.retryingJobFailed = true
      this.manifestError = ''
      this.retryJobFailedResult = ''
      try {
        const body = { limit: 25 }
        if (this.retryFailedCursor) body.cursor = this.retryFailedCursor
        if (this.retryFailedManifestSource) body.manifest_source = this.retryFailedManifestSource
        const r = await axios.post(
          `${this.apiBase()}/admin/snapshot/ingestion-jobs/${encodeURIComponent(this.selectedManifestJobId)}/retry-failed`,
          body
        )
        const data = r.data || {}
        const retriedIds = new Set((data.retried || []).map((item) => item.photo_id))
        this.manifestPhotos = this.manifestPhotos.map((item) => (
          retriedIds.has(item.photo_id) ? { ...item, status: 'claimed', failure_reason: '' } : item
        ))
        this.retryFailedCursor = data.cursor || ''
        this.retryFailedManifestSource = data.manifest_source || ''
        const pieces = [`Retried ${data.retried_count || 0} failed photos`]
        if (data.error_count) pieces.push(`${data.error_count} errors`)
        if (data.has_more) pieces.push('more rows remain')
        this.retryJobFailedResult = pieces.join(' · ')
        await this.loadOperations()
      } catch (e) {
        this.manifestError = 'Unable to retry failed photos for this job.'
      } finally {
        this.retryingJobFailed = false
      }
    },
    async queueFailedManifestRetry() {
      if (!this.selectedManifestJobId || this.queueingJobRetry) return
      this.queueingJobRetry = true
      this.manifestError = ''
      this.retryJobFailedResult = ''
      try {
        const body = { async: true }
        if (this.retryFailedCursor) body.cursor = this.retryFailedCursor
        if (this.retryFailedManifestSource) body.manifest_source = this.retryFailedManifestSource
        const r = await axios.post(
          `${this.apiBase()}/admin/snapshot/ingestion-jobs/${encodeURIComponent(this.selectedManifestJobId)}/retry-failed`,
          body
        )
        const runId = r.data && r.data.run_id
        this.retryJobFailedResult = runId
          ? `Queued failed-photo retry job ${runId}`
          : 'Queued failed-photo retry job'
        await this.loadOperations()
      } catch (e) {
        this.manifestError = 'Unable to queue failed-photo retry job.'
      } finally {
        this.queueingJobRetry = false
      }
    },
    auditDetails(event) {
      const metadata = event && event.metadata ? event.metadata : {}
      const entries = Object.entries(metadata)
        .filter(([key]) => !this.isSensitiveAuditKey(key))
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .slice(0, 5)
      if (!entries.length) return ''
      return entries
        .map(([key, value]) => `${key}: ${this.auditValue(value)}`)
        .join(' · ')
    },
    auditActor(event) {
      return this.auditValue(event && event.actor ? event.actor : 'system')
    },
    auditResource(event) {
      if (!event) return ''
      return `${this.auditValue(event.resource_type || 'resource')}:${this.auditValue(event.resource_id || '')}`
    },
    auditValue(value) {
      if (Array.isArray(value)) return value.map((item) => this.auditValue(item)).join(', ')
      if (value && typeof value === 'object') return this.redactAuditString(JSON.stringify(value))
      return this.redactAuditString(String(value))
    },
    redactAuditString(value) {
      return value
        .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted-email]')
        .replace(/(?:collections\/[^/\s]+\/photos\/[^/\s]+\/(?:full|thumb)\.jpg|[0-9a-f-]{36}\/(?:fullres|thumbnails)\/[^\s'"<>]+)/gi, '[redacted-object-key]')
    },
    isSensitiveAuditKey(key) {
      return /(email|name|stripe|payment|intent|secret|token|password|s3|key|face)/i.test(String(key || ''))
    },
    statusClass(status) {
      if (status === 'failed') return 'bg-red-50 text-red-700'
      if (status === 'skipped') return 'bg-amber-50 text-amber-700'
      if (status === 'running' || status === 'presigned') return 'bg-blue-50 text-blue-700'
      if (status === 'complete') return 'bg-green-50 text-green-700'
      return 'bg-gray-50 text-gray-700'
    },
    photoStatusClass(status) {
      if (status === 'failed' || status === 'deleted') return 'bg-red-50 text-red-700'
      if (status === 'hidden') return 'bg-amber-50 text-amber-700'
      if (status === 'ready' || status === 'indexed') return 'bg-green-50 text-green-700'
      if (status === 'stored' || status === 'claimed') return 'bg-blue-50 text-blue-700'
      return 'bg-gray-50 text-gray-700'
    },
  },
}
</script>
